import { centsToPtbr } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function fmtCents(cents: number): string {
  return `R$ ${centsToPtbr(cents)}`;
}

/**
 * Visual padrão dos cards do resumo da proposta:
 * cantos arredondados, borda suave e sombra discreta.
 */
export const RESUME_CARD_CLASS =
  'rounded-xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-6px_rgba(15,23,42,0.08)]';

/** Card padronizado do resumo (section com o visual definido em RESUME_CARD_CLASS). */
export function ResumeCard({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
  return <section className={cn(RESUME_CARD_CLASS, className)} {...props} />;
}

/** Cabeçalho padronizado de card: título em destaque, ícone opcional e área à direita. */
export function ResumeCardHeader({
  title,
  icon,
  subtitle,
  className,
  children,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  subtitle?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn('flex items-center gap-2.5 border-b border-gray-100 px-5 py-4', className)}>
      {icon}
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-gray-900">{title}</h3>
        {subtitle && <p className="truncate text-[11px] text-gray-500">{subtitle}</p>}
      </div>
      {children && <div className="ml-auto shrink-0">{children}</div>}
    </div>
  );
}

/**
 * Mini-card horizontal reutilizável (ícone + label pequena + valor em destaque).
 * Substitui os InfoItem/MiniCard duplicados entre os cards de negociação e pagamento.
 */
export function InfoMiniCard({
  icon,
  label,
  value,
  subtitle,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  className?: string;
}) {
  return (
    <div className={cn(RESUME_CARD_CLASS, 'flex min-w-0 items-start gap-3 px-4 py-4', className)}>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-50">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="break-words text-[10px] font-bold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p className="mt-0.5 break-words text-base font-bold tabular-nums text-gray-950">{value}</p>
        {subtitle && <p className="mt-0.5 break-words text-[11px] text-gray-500">{subtitle}</p>}
      </div>
    </div>
  );
}

export function SectionTitle({ children }: { children: React.ReactNode }) {
  return <p className="text-[10px] font-bold uppercase tracking-wide text-gray-700">{children}</p>;
}

export function SummaryRow({
  label,
  value,
  subtitle,
  bold,
  green,
}: {
  label: string;
  value: string;
  subtitle?: React.ReactNode;
  bold?: boolean;
  green?: boolean;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 text-[11px] leading-4">
      <span
        className={`min-w-0 break-words ${
          bold ? 'font-semibold text-gray-950' : green ? 'text-green-600' : 'text-gray-600'
        }`}
      >
        {label}
      </span>
      <div className="max-w-full text-right">
        <span
          className={`whitespace-normal break-words tabular-nums ${bold ? 'font-semibold text-gray-950' : 'text-gray-900'} ${green ? 'text-green-600' : ''}`}
        >
          {value}
        </span>
        {subtitle && (
          <p className="break-words text-[10px] leading-3.5 text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

export function renderDiscountTag(discountInCents: number) {
  return discountInCents > 0 ? (
    <>
      {' | '}
      <span className="text-green-600">Desconto {fmtCents(discountInCents)}</span>
    </>
  ) : null;
}
